import { lazy, Suspense, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { OAuthOnboardingProvider } from "@/components/OAuthOnboardingProvider";
import { CreateListingProvider } from "@/contexts/CreateListingContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PostAuthRedirect } from "@/components/PostAuthRedirect";
import { CookieConsent } from "@/components/CookieConsent";
import { SearchAtlasLoader } from "@/components/SearchAtlasLoader";
import { PageMeta } from "@/components/PageMeta";
import { PageLoadingSkeleton } from "@/lib/lazyComponents";

// Only the landing page is eagerly loaded for fast initial render
import Index from "./pages/Index";

// All other pages are lazy-loaded to reduce initial JS bundle
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const MyListings = lazy(() => import("./pages/MyListings"));
const ViewListing = lazy(() => import("./pages/ViewListing"));
const PreviewListing = lazy(() => import("./pages/PreviewListing"));
const BeforeAfter = lazy(() => import("./pages/BeforeAfter"));
const QrRedirect = lazy(() => import("./pages/QrRedirect"));
const QrAnalytics = lazy(() => import("./pages/QrAnalytics"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Features = lazy(() => import("./pages/Features"));
const FeatureDetail = lazy(() => import("./pages/FeatureDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const GDPR = lazy(() => import("./pages/GDPR"));
const UsageDashboard = lazy(() => import("./pages/UsageDashboard"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Compare = lazy(() => import("./pages/Compare"));const ComparisonVs = lazy(() => import('./pages/ComparisonVs'));const About = lazy(() => import("./pages/About"));
const SinglePropertyKit = lazy(() => import("./pages/SinglePropertyKit"));
const DemoPage = lazy(() => import("./pages/DemoPage"));
const AiRealEstateDescriptions = lazy(() => import("./pages/AiRealEstateDescriptions"));
const AiVirtualStaging = lazy(() => import("./pages/AiVirtualStaging"));
const RealEstateMarketingTools = lazy(() => import("./pages/RealEstateMarketingTools"));
const HowToWriteListingDescription = lazy(() => import("./pages/HowToWriteListingDescription"));
const RealEstateMarketingIdeas = lazy(() => import("./pages/RealEstateMarketingIdeas"));
const RealtorMarketingTools = lazy(() => import("./pages/RealtorMarketingTools"));
const BlogPost = lazy(() => import("./pages/BlogPost"));const CinematicVideo = lazy(() => import('./pages/CinematicVideo'));const NotFound = lazy(() => import("./pages/NotFound"));
const HowItWorksVideo = lazy(() => import("./pages/HowItWorksVideo"));
const AiPhotoTransformation = lazy(() => import("./pages/AiPhotoTransformation"));
const AiDeclutterPhotos = lazy(() => import("./pages/AiDeclutterPhotos"));
const RemovePhotographerReflections = lazy(() => import("./pages/RemovePhotographerReflections"));
const BlurFamilyPhotos = lazy(() => import("./pages/BlurFamilyPhotos"));
const BestVirtualStagingSoftware = lazy(() => import("./pages/BestVirtualStagingSoftware"));
const BestAiForRealEstate = lazy(() => import("./pages/BestAiForRealEstate"));
const BestAiForRealEstateListings = lazy(() => import("./pages/BestAiForRealEstateListings"));
const AiListingGenerator = lazy(() => import("./pages/AiListingGenerator"));
const RealEstateDescriptionExamples = lazy(() => import("./pages/RealEstateDescriptionExamples"));
const HowToUseAiInRealEstate = lazy(() => import("./pages/HowToUseAiInRealEstate"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const PrivateVideo = lazy(() => import("./pages/PrivateVideo"));

const queryClient = new QueryClient();

// Private app/utility routes get a tab title and a noindex robots meta so
// they stay out of search results (robots.txt also disallows them).
const privatePage = (title: string, description: string, page: ReactNode) => (
  <>
    <PageMeta title={title} description={description} noindex />
    {page}
  </>
);

console.log('[ListBright] App.tsx module loaded');

const App = () => {
  console.log('[ListBright] App component rendering');
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <PostAuthRedirect />
        <SearchAtlasLoader />
        <OAuthOnboardingProvider>
          <CreateListingProvider>
          <Suspense fallback={<PageLoadingSkeleton />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={privatePage("Sign In or Create Account", "Sign in to your ListBright account to create AI-powered real estate listings.", <Auth />)} />
            <Route path="/profile" element={privatePage("Your Profile", "Manage your ListBright account settings and preferences.", <Profile />)} />
            <Route path="/my-listings" element={privatePage("My Listings", "View and manage your ListBright property listings.", <MyListings />)} />
            <Route path="/listing/:id" element={privatePage("Your Listing", "View your generated ListBright property listing.", <ViewListing />)} />
            <Route path="/preview/:id" element={<PreviewListing />} />
            <Route path="/before-after/:id" element={<BeforeAfter />} />
            <Route path="/qr/:id" element={privatePage("Redirecting…", "Taking you to the listing.", <QrRedirect />)} />
            <Route path="/qr-analytics" element={privatePage("QR Code Analytics", "Track scans and engagement for your listing QR codes.", <QrAnalytics />)} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/features/:slug" element={<FeatureDetail />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/gdpr" element={<GDPR />} />
            <Route path="/usage" element={privatePage("Usage Dashboard", "Track your ListBright plan usage and credits.", <UsageDashboard />)} />
            <Route path="/accept-invite" element={privatePage("Accept Team Invite", "Join your team on ListBright.", <AcceptInvite />)} />
            <Route path="/admin" element={privatePage("Admin Dashboard", "ListBright administration.", <AdminDashboard />)} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/compare/:slug" element={<ComparisonVs />} />
            <Route path="/cinematic-video" element={<CinematicVideo />} />
            <Route path="/about" element={<About />} />
            <Route path="/single-property-kit" element={<SinglePropertyKit />} />
            <Route path="/demo/:token" element={<DemoPage />} />
            <Route path="/unsubscribe" element={privatePage("Email Preferences", "Manage your ListBright email subscription preferences.", <Unsubscribe />)} />
            <Route path="/ai-real-estate-descriptions" element={<AiRealEstateDescriptions />} />
            <Route path="/ai-virtual-staging" element={<AiVirtualStaging />} />
            <Route path="/real-estate-marketing-tools" element={<RealEstateMarketingTools />} />
            <Route path="/how-to-write-a-real-estate-listing-description" element={<HowToWriteListingDescription />} />
            <Route path="/real-estate-marketing-ideas" element={<RealEstateMarketingIdeas />} />
            <Route path="/real-estate-marketing-software" element={<RealtorMarketingTools />} />
            {/* Legacy realtor URLs → canonical generalist URL */}
            <Route path="/realtor-marketing-tools" element={<Navigate to="/real-estate-marketing-software" replace />} />
            <Route path="/tools-for-realtors" element={<Navigate to="/real-estate-marketing-software" replace />} />
            <Route path="/how-it-works" element={<HowItWorksVideo />} />
            <Route path="/how-listbright-works" element={<HowItWorksVideo />} />
            <Route path="/ai-photo-transformation" element={<AiPhotoTransformation />} />
            <Route path="/ai-real-estate-photo-transformation" element={<AiPhotoTransformation />} />
            <Route path="/ai-declutter-photos" element={<AiDeclutterPhotos />} />
            <Route path="/remove-photographer-reflections" element={<RemovePhotographerReflections />} />
            <Route path="/blur-family-photos" element={<BlurFamilyPhotos />} />
            <Route path="/best-virtual-staging-software" element={<BestVirtualStagingSoftware />} />
            <Route path="/best-ai-for-real-estate" element={<BestAiForRealEstate />} />
            <Route path="/best-ai-for-real-estate-listings" element={<BestAiForRealEstateListings />} />
            <Route path="/ai-listing-generator" element={<AiListingGenerator />} />
            <Route path="/real-estate-description-examples" element={<RealEstateDescriptionExamples />} />
            <Route path="/how-to-use-ai-in-real-estate" element={<HowToUseAiInRealEstate />} />
            <Route
              path="/v/lb-walkthrough-7f3a91c2"
              element={privatePage("Private Video", "Private video shared via link.", <PrivateVideo />)}
            />
            <Route
              path="/.lovable/oauth/consent"
              element={privatePage(
                "Authorize app",
                "Approve or deny access for an external app to use ListBright as you.",
                <OAuthConsent />,
              )}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </CreateListingProvider>
        </OAuthOnboardingProvider>
        <CookieConsent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;